/**
 * BoardV3 Tasks Layer
 * Renders all visible tasks in their positions.
 * Reads detailLevel/isDetailResizing from boardLayoutStore.
 * Handles task resize and context menu actions directly.
 */

import { memo, useCallback } from 'react';
import type { Employee } from '../../types';
import { BoardV3DraggableTaskBox } from './taskbox/BoardV3DraggableTaskBox';
import { getTaskDayIndex } from '../../lib/dateUtils';
import { useTasksByColumn, useColumnYOffsets, useRowAssignments, useDayWidth, useRowHeight, useHeaderHeight, useDetailLevel } from './BoardLayoutContext';
import { useColumnNames, useEmployees, useScheduledTasks, useBoardStartDate, useRoadmapActions } from '../../stores/hooks';
import { scheduleAfter } from '../stores/scheduleUtils';
import { popupStore } from '../stores/popupStore';

interface BoardV3TasksLayerProps {
  draggingTaskId: string | null;
}

export const BoardV3TasksLayer = memo(function BoardV3TasksLayer({
  draggingTaskId,
}: BoardV3TasksLayerProps) {
  const tasksByColumn = useTasksByColumn();
  const columns = useColumnNames();
  const rowAssignments = useRowAssignments();
  const columnYOffsets = useColumnYOffsets();
  const employees = useEmployees();
  const scheduledTasks = useScheduledTasks();
  const dayWidth = useDayWidth();
  const rowHeight = useRowHeight();
  const headerHeight = useHeaderHeight();
  const boardStartDate = useBoardStartDate();
  const detailLevel = useDetailLevel();
  const { resizeTask } = useRoadmapActions();

  const handleTaskResize = useCallback(
    (taskId: string, newDurationDays: number, startDayDelta?: number) => {
      scheduleAfter(() => resizeTask(taskId, newDurationDays, startDayDelta));
    },
    [resizeTask]
  );

  const handleTaskContextMenu = useCallback((info: { taskId: string; x: number; y: number; dayOffset: number }) => {
    popupStore.send({ type: 'popup.openTaskMenu', ...info });
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
      }}
    >
      {columns.map((column) => {
        const tasks = tasksByColumn[column] || [];
        const colAssignments = rowAssignments[column];
        const yOffset = columnYOffsets[column] ?? 0;

        return tasks.map((task) => {
          const row = colAssignments?.get(task.id) ?? 0;
          const duration = scheduledTasks.get(task.id)?.effectiveDays ?? 10;

          const x = getTaskDayIndex(task.startDate, boardStartDate) * dayWidth;
          const y = headerHeight + yOffset + row * rowHeight;
          const width = duration * dayWidth;
          const height = rowHeight;

          const assignees = task.assigneeIds
            .map((id) => employees.find((e) => e.id === id))
            .filter((e): e is Employee => e !== undefined);

          const scheduled = scheduledTasks.get(task.id);
          const isDragging = task.id === draggingTaskId;

          return (
            <BoardV3DraggableTaskBox
              key={task.id}
              task={task}
              x={x}
              y={y}
              width={width}
              height={height}
              dayWidth={dayWidth}
              assignees={assignees}
              scheduled={scheduled}
              detailLevel={detailLevel}
              onResize={handleTaskResize}
              onContextMenu={(e, dayOffset) => {
                handleTaskContextMenu({ taskId: task.id, x: e.clientX, y: e.clientY, dayOffset });
              }}
              isDragging={isDragging}
            />
          );
        });
      })}
    </div>
  );
});
